
import React from 'react'
import { compose } from 'widgets-shared/core'
import { withHotspotTracking } from 'smartstream-widgets/enhancers/withHotspotTracking'
import { withMuiThemeProvider } from 'smartstream-widgets/enhancers/withMuiThemeProvider'
import { withTranslations } from 'widgets-shared/core'
import { composeWidget, createWidgetInitFunction } from 'widgets-shared/core'
import { Poller } from 'widgets-shared/services'
import Carousel from 'smartstream-widgets/controls/Carousel/Carousel'
import PredictedScoreComponent from './PredictedScoreComponent'
import PostMatchComponent from '../../components/PostMatchComponent/PostMatchComponent'
import PredictedScoreFooter from './PredictedScoreFooter/PredictedScoreFooter'
import { getOnSlideRenderHandler } from 'smartstream-widgets/utils/widgetDataChanged'
import { fallbackChecker } from 'smartstream-widgets/utils/fallbackChecker'
import { isPostMatchStatus } from 'smartstream-widgets/utils/isPostMatchStatus'

import { withStyles, createStyles } from '@mui/styles'
import { Theme } from '@mui/material/styles'
import { LoggerFunction, TranslationFunction, ConfigOptions } from 'smartstream-widgets/types/sharedModule/core'
import { WidgetInitializationParameters } from 'widgets-shared/core'
import { Props, State, PredictedScoreData, PredictedFixture } from './types'
import { getDefaultTheme } from 'smartstream-widgets/utils/getDefaultTheme'

const styles = (theme: Theme) => createStyles({
  carouselButton: {
    color: theme.palette.action.active,
    '&:disabled': {
      color: theme.palette.action.disabled,
      opacity: 1
    }
  },
  container: {
    backgroundColor: theme.palette.background.default,
    boxSizing: 'border-box',
    height: '100%',
    position: 'relative',
    width: '100%'
  },
  controlCaptions: {
    fontSize: 9
  },
  controlIconsSvg: {
    fontSize: 12
  },
  navigationElements: {
    background: 'transparent',
    bottom: 9,
    position: 'absolute',
    width: '100%',
    zIndex: 5
  },
  scorePredicted: {
    [theme.breakpoints.up('c420')]: {
      fontSize: 44
    },
    [theme.breakpoints.up('c576')]: {
      fontSize: 46
    }
  },
  scoreLabel: {
    [theme.breakpoints.up('c420')]: {
      fontSize: 9
    },
    [theme.breakpoints.up('c576')]: {
      fontSize: 10
    }
  },
  teamContent: {
    [theme.breakpoints.up('c420')]: {
      maxWidth: 150
    },
    [theme.breakpoints.up('c576')]: {
      maxWidth: 170
    }
  },
  teamNames: {
    [theme.breakpoints.up('c420')]: {
      fontSize: 14
    },
    [theme.breakpoints.up('c576')]: {
      fontSize: 18
    }
  },
  title: {
    [theme.breakpoints.up('c576')]: {
      fontSize: 16
    }
  },
  logoFlagSize: {
    [theme.breakpoints.up('c420')]: {
      height: 48,
      width: 100
    },
    [theme.breakpoints.up('c576')]: {
      height: 54,
      width: 130
    },
    [theme.breakpoints.up('c728')]: {
      height: 54,
      width: 130
    }
  },
  teamLogoSize: {
    [theme.breakpoints.up('c420')]: {
      height: 40,
      width: 40
    },
    [theme.breakpoints.up('c576')]: {
      height: 46,
      width: 46
    },
    [theme.breakpoints.up('c728')]: {
      width: 48,
      height: 48
    }
  },
  singleContainer: {
    height: '100%'
  }
})

export class PredictedScoreWidget extends React.Component<Props, State> {
  poller: Poller
  containerRef: React.RefObject<HTMLDivElement>

  constructor(props: Props) {
    super(props)
    const {
      config,
      data: {
        fixtures,
        disclaimer,
        bookmakers,
      } = {},
      renderParameters: {
        group,
        productName,
        region,
        country,
        fixtureSource,
        themeMode,
        postMatchTimeFrame
      } = {},
      routingKey
    } = props
    this.containerRef = React.createRef<HTMLDivElement>()

    const pollerOptions = {
      url: config['POLL_URL'],
      frequency: config['POLLING_FREQUENCY'],
      queryParams: (productName && { group, productName, region, country, fixtureSource, themeMode, postMatchTimeFrame }) || {},
      onError: this.onError.bind(this) as this['onError'],
      onPoll: this.onPoll.bind(this) as this['onPoll'],
      routingKey
    }
    this.poller = new Poller(pollerOptions)

    const isSingleFixture = fixtures?.length === 1

    this.state = {
      widgetData: fixtures,
      ctaOfferIsDisabledOnWidget: false,
      configOptions: {
        disclaimer,
        bookmakers,
      },
      isSingleFixture
    }
  }

  hideCtaOfferOnWidget() {
    this.setState({ ctaOfferIsDisabledOnWidget: true })
  }

  componentWillUnmount() {
    this.poller.stopPolling()
  }

  componentDidMount() {
    fallbackChecker.call(this)
  }

  onPoll(responseData: PredictedScoreData): void {
    const { fixtures, disclaimer, footerLink, bookmakers } = responseData
    const configOptions = {
      disclaimer,
      footerLink,
      bookmakers,
    }
    this.setState(() => ({
      widgetData: fixtures,
      configOptions
    }))
  }

  onError(error: Error): void {
    const { logger } = this.props
    const loggerError = logger.error.bind(logger) as LoggerFunction
    loggerError(error)
  }

  getFixture(widgetData: PredictedFixture[], configOptions: ConfigOptions) {
    const { disclaimer, footerLink, bookmakers } = configOptions
    const {
      classes
    } = this.props
    return widgetData.map((data, index) => (
      <div key={data.fixtureId} data-testid="predicted-score-single-container" className={classes.singleContainer}>
        {
          isPostMatchStatus(data.matchSummaries) ?
            <PostMatchComponent
              key={index}
              classes={{
                scorePredicted: classes.scorePredicted,
                scoreLabel: classes.scoreLabel,
                teamContent: classes.teamContent,
                title: classes.title,
                logoFlagSize: classes.logoFlagSize,
                teamLogoSize: classes.teamLogoSize
              }}
              containerRef={this.containerRef}
              data={data} /> :
            <PredictedScoreComponent
              key={index}
              containerRef={this.containerRef}
              data={data}
              bookmakers={bookmakers}
              hideCtaOfferOnWidget={() => this.hideCtaOfferOnWidget()}
              ctaOfferIsDisabledOnWidget={this.state.ctaOfferIsDisabledOnWidget} />
        }
        {
          !isPostMatchStatus(data.matchSummaries) &&
            <PredictedScoreFooter
              key={data.fixtureId}
              disclaimer={disclaimer}
              footerLink={footerLink}
              isSingleFixture={this.state.isSingleFixture}
            />
        }
      </div>
    ))
  }

  render() {
    const { widgetData, configOptions } = this.state
    const {
      config: {
        BASE_ASSET_PATH
      } = {},
      classes,
      translations
    } = this.props

    const stylesCarousel = {
      carouselButton: classes.carouselButton,
      controlCaptions: classes.controlCaptions,
      controlIconsSvg: classes.controlIconsSvg,
      navigationElements: classes.navigationElements
    }

    return (
      <>
        {
          widgetData && widgetData.length > 0 &&
          <div className={classes.container} ref={this.containerRef}>
            {
              widgetData.length > 1 ?
                <Carousel
                  classes={stylesCarousel}
                  iconViewBox='0 0 8 12'
                  isContinuous={false}
                  leftControlText={translations.prevCarouselArrow}
                  onSlideRender={getOnSlideRenderHandler(this.props, this.state)}
                  rightControlText={translations.nextCarouselArrow}
                  showIndicatorDots={false}
                  svgIconLinkHrefLeft={`${BASE_ASSET_PATH}/img/icons.svg#new-arrow-left`}
                  svgIconLinkHrefRight={`${BASE_ASSET_PATH}/img/icons.svg#new-arrow-right`}
                >
                  {this.getFixture(widgetData, configOptions)}
                </Carousel> :
                this.getFixture(widgetData, configOptions)
            }
          </div>
        }
      </>
    )
  }
}

const PredictedScoreEnhanced = compose(
  withMuiThemeProvider,
  withHotspotTracking,
  withStyles(styles, { name: 'PredictedScore', defaultTheme: getDefaultTheme }),
  withTranslations((t: TranslationFunction) => {
    return {
      nextCarouselArrow: t('nextArrow.translation', {
        defaultValue: 'NEXT',
        context: 'Text to display as label for "next" navigation arrow in Predicted Score widget'
      }),
      prevCarouselArrow: t('prevArrow.translation', {
        defaultValue: 'PREV',
        context: 'Text to display as label for "previous" navigation arrow in Predicted Score widget'
      })
    }
  })
)(PredictedScoreWidget) as React.Component

export default (widgetInitializationParameters: WidgetInitializationParameters) => {
  return composeWidget(PredictedScoreEnhanced, widgetInitializationParameters) as React.ElementType
}

createWidgetInitFunction(PredictedScoreEnhanced, 'PredictedScore')
